import type React from 'react';
import { useId } from 'react';
import { Check, CircleCheck } from 'lucide-react';
import { cn } from '@lib/utils';
import type { PollOptionRead } from '@models/api/poll';
import { POLL_COPY } from './pollCopy';
import type { PollRowState } from './pollDisplay';

export interface PollOptionControlProps {
  option: PollOptionRead;
  state: PollRowState;
  multiple: boolean;
  chosen: boolean;
}

/**
 * The option's control as drawn (polls-B1, B2, B3, B5): a 20px square (Pick one or more) or circle
 * (Pick one), 2px control border on white; chosen, filled teal-ink with a white tick; dimmed once
 * ended. A picture only (aria-hidden): answering (FE-1b) lays a radio or checkbox over the row
 * (`PollOptionRow`'s `answer`); the composer passes its own through `renderOptionControl`. One-off, built with today's tokens (TECH-197 "Needs
 * the design system": ui/checkbox and ui/radio-group are the stand-ins, and Radix's radio cannot be
 * unchecked, which the ticket asks for).
 */
export const PollOptionMark: React.FC<PollOptionControlProps> = ({ state, multiple, chosen }) => {
  if (state === 'closed-empty') return null;
  return (
    <span
      aria-hidden="true"
      data-testid="poll-option-mark"
      className={cn(
        'flex size-5 shrink-0 items-center justify-center border-2',
        multiple ? 'rounded-md' : 'rounded-full',
        chosen ? 'border-mb-teal-ink bg-mb-teal-ink' : 'border-mb-control-border bg-white',
        state === 'result-closed' && 'opacity-[0.55]',
      )}
    >
      {chosen && <Check className="size-3.5 text-white" strokeWidth={3} />}
    </span>
  );
};

/** The row as an answer the viewer can tap (FE-1b): a radio (Pick one) or a checkbox. */
export interface PollOptionAnswer {
  role: 'radio' | 'checkbox';
  /** Roving focus in a radio group: only the checked (or first) radio is in the tab order. */
  tabIndex: 0 | -1;
  onToggle: () => void;
}

export interface PollOptionRowProps {
  option: PollOptionRead;
  state: PollRowState;
  /** The viewer's own pick (held ones too). */
  chosen: boolean;
  /** A leading option of an ended poll (every tied leader). */
  leader: boolean;
  share: number;
  /** The drawn mark, a picture only, inside the row's words. */
  mark?: React.ReactNode;
  /**
   * An interactive control the caller draws (the composer's, FE-2b): outside the part hidden from
   * screen readers while results show, so it is never focusable but hidden.
   */
  control?: React.ReactNode;
  /** Makes the whole row the viewer's answer control. */
  answer?: PollOptionAnswer;
  /** The bar grows in from nothing (results just showed after answering, F3); motion-safe only. */
  growIn?: boolean;
}

/**
 * One option (one-off, TECH-197 § Component map): live / ticked / result open / result closed /
 * closed with no answers. The bar is a full-height `mb-teal-light` fill whose width is the share,
 * never the frame's drawn width; it is a `meter` named by the option's text (aria-labelledby) with
 * the value "6 answers, 60%", read as "Cais do Sodré, 6 answers, 60%", so the row's visible words
 * are hidden from screen readers while it shows. What the row draws without words (the viewer's
 * filled tick, an ended poll's circle-check) is said after it: "Your answer", "Most answers".
 * When the row is the viewer's answer control, the control carries the name, state and result,
 * and the meter is hidden: one announcement per option.
 */
export const PollOptionRow: React.FC<PollOptionRowProps> = ({
  option,
  state,
  chosen,
  leader,
  share,
  mark,
  control,
  answer,
  growIn = false,
}) => {
  const withResult = state === 'result-open' || state === 'result-closed';
  const ownBorder = chosen && state !== 'result-closed' && state !== 'closed-empty';
  const count = option.votes_count ?? 0;
  const textId = useId();
  const resultId = useId();
  const markedLeader = leader && state === 'result-closed';
  // An answer control says "checked" itself; what else the row draws without words is said after it.
  const said = withResult
    ? [chosen && !answer && POLL_COPY.yourAnswer, markedLeader && POLL_COPY.mostAnswers].filter(
        Boolean,
      )
    : [];

  return (
    <li
      data-testid="poll-option-row"
      data-state={state}
      data-chosen={chosen || undefined}
      // Inside a radio group a list item is not an option: the radio is.
      role={answer ? 'none' : undefined}
      className={cn(
        // Exactly 48 with its border on one line: min-h 48 is border-box (borders inside it), and
        // 12 + 20 + 12 + the 1px (or 1.5px) borders stays under it. A long option grows (B9: block
        // padding 12). py-3.5 drew 50 (14 + 20 + 14 + 2), browser walk 07:33.
        'relative flex min-h-12 items-start gap-3 overflow-hidden rounded-xl px-3.5 py-3',
        state === 'closed-empty' ? 'bg-transparent' : 'bg-white',
        ownBorder ? 'border-[1.5px] border-mb-teal-ink' : 'border border-mb-border',
      )}
    >
      {withResult && (
        <div
          role="meter"
          // An answer control already says the result (aria-describedby): the meter is then a
          // picture only, so each option is read once, and no meter sits inside the radio group.
          aria-hidden={answer ? true : undefined}
          aria-labelledby={textId}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={share}
          aria-valuetext={POLL_COPY.optionResult(count, share)}
          className="absolute inset-0"
        >
          <div
            data-testid="poll-option-bar"
            className={cn(
              'h-full bg-mb-teal-light motion-safe:transition-[width] motion-safe:duration-300',
              growIn && 'motion-safe:animate-poll-bar-grow',
            )}
            style={{ width: `${share}%` }}
          />
        </div>
      )}
      {control}
      <div
        className="relative flex min-w-0 flex-1 items-start gap-3"
        // The answer control carries the row's name and state, the meter its result.
        aria-hidden={withResult || answer ? true : undefined}
      >
        {mark}
        <span
          id={textId}
          dir="auto"
          className={cn(
            'min-w-0 flex-1 text-[15px] leading-[18px] font-normal wrap-break-word',
            state === 'closed-empty' ? 'text-mb-dark-gray' : 'text-mb-ink',
          )}
        >
          {option.text}
        </span>
        {withResult && (
          <span
            data-testid="poll-option-share"
            className={cn(
              'flex shrink-0 items-center gap-1.5 text-sm leading-[18px] tabular-nums',
              markedLeader
                ? 'font-bold text-mb-teal-deep'
                : chosen && state === 'result-open'
                  ? 'font-semibold text-mb-teal-deep'
                  : 'font-medium text-mb-gray-dark',
            )}
          >
            {markedLeader && (
              <CircleCheck
                data-testid="poll-option-leader"
                className="size-[18px] text-mb-teal-ink"
                strokeWidth={2.25}
              />
            )}
            {POLL_COPY.shareAndCount(share, count)}
          </span>
        )}
      </div>
      {said.length > 0 && <span className="sr-only">{said.join(', ')}</span>}
      {answer && (
        <>
          {withResult && (
            <span id={resultId} hidden>
              {POLL_COPY.optionResult(count, share)}
            </span>
          )}
          <button
            type="button"
            role={answer.role}
            aria-checked={chosen}
            aria-labelledby={textId}
            aria-describedby={withResult ? resultId : undefined}
            tabIndex={answer.tabIndex}
            data-poll-answer=""
            onClick={event => {
              // The feed card opens the poll on a click anywhere else.
              event.stopPropagation();
              answer.onToggle();
            }}
            className="absolute inset-0 cursor-pointer rounded-xl focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mb-teal-ink"
          />
        </>
      )}
    </li>
  );
};
